import React, { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Badge,
  Button,
  DateTimePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Grid,
  Icon,
  InputLabel,
  Separator,
  Stack,
  Text,
} from '@eluve/components';
import { TenantSelector, TenantUserSelector } from '@eluve/smart-blocks';

import { ExperimentMatch, ExperimentMatchCard } from './ExperimentMatchCard';

const toLocalTimeString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

type TestAssignmentResult = {
  allMatches: ExperimentMatch[];
  winner: ExperimentMatch | null;
  injectedExperiment: ExperimentMatch | null;
};

interface TestAssignmentSectionProps {
  experimentId: string;
}

type SelectedTenant = {
  id: string;
  name: string;
};

export const TestAssignmentSection: React.FC<TestAssignmentSectionProps> = ({
  experimentId,
}) => {
  const apiClient = useApiClient();

  const [open, setOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<SelectedTenant | null>(
    null,
  );
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [simulatedDateTime, setSimulatedDateTime] = useState<
    Date | undefined
  >();
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<TestAssignmentResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleTest = async () => {
    if (!selectedTenant || !selectedUserId) {
      setError('Tenant and User are required');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await apiClient.experiments.testAssignment({
        body: {
          tenantId: selectedTenant.id,
          userId: selectedUserId,
          injectExperimentId: experimentId,
          simulatedLocalTime: simulatedDateTime
            ? toLocalTimeString(simulatedDateTime)
            : undefined,
        },
      });

      if (response.status === 200) {
        setResult(response.body as TestAssignmentResult);
      } else {
        setError('Failed to test assignment');
      }
    } catch {
      setError('Failed to test assignment');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setResult(null);
      setError(null);
    }
  };

  const handleTenantSelected = (tenant: SelectedTenant) => {
    setSelectedTenant(tenant);
    setSelectedUserId(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Icon name="FlaskConical" size="auto" />
          Test Assignment
        </Button>
      </DialogTrigger>
      <DialogContent size="4xl" className="max-h-[85vh] overflow-visible">
        <DialogHeader>
          <DialogTitle>Test Assignment</DialogTitle>
        </DialogHeader>

        <Stack gap={4}>
          <Text size="xs" color="tertiary">
            Test which experiment a user would be assigned to. This experiment
            will be included even if not active.
          </Text>

          <Grid columns={1} gap={4} className="md:grid-cols-3">
            <InputLabel label="Tenant *">
              <TenantSelector
                selectedTenant={selectedTenant}
                onTenantSelected={handleTenantSelected}
                placeholder="Search for a tenant"
              />
            </InputLabel>

            <InputLabel label="User *">
              {selectedTenant ? (
                <TenantUserSelector
                  tenantId={selectedTenant.id}
                  selectedUserId={selectedUserId ?? undefined}
                  onUserSelected={setSelectedUserId}
                />
              ) : (
                <Stack
                  className="h-10 rounded-md border border-border-primary bg-bg-secondary px-4 text-text-tertiary"
                  direction="horizontal"
                  align="center"
                >
                  Select a tenant first
                </Stack>
              )}
            </InputLabel>

            <InputLabel label="Simulated Local Time">
              <DateTimePicker
                dateTime={simulatedDateTime}
                onDateTimeChange={setSimulatedDateTime}
                placeholder="Select date and time"
              />
            </InputLabel>
          </Grid>

          {error && (
            <div className="w-full rounded-lg border border-border-negative-subtle bg-bg-negative-subtle p-3">
              <Text size="md">{error}</Text>
            </div>
          )}

          {result && (
            <div className="w-full rounded-lg border border-border-primary bg-bg-gray-subtle p-4">
              <Stack gap={4}>
                <Stack gap={2} align="center" direction="horizontal">
                  <Text size="md" inline weight="semibold">
                    Results
                  </Text>
                  <Badge variant="secondary">
                    {result.allMatches.length} match
                    {result.allMatches.length !== 1 ? 'es' : ''}
                  </Badge>
                </Stack>

                {result.allMatches.length === 0 ? (
                  <Text size="md" color="tertiary">
                    User is not allocated to any experiments.
                  </Text>
                ) : (
                  <Stack gap={3}>
                    {result.allMatches.map((match) => (
                      <ExperimentMatchCard
                        key={match.experimentId}
                        match={match}
                      />
                    ))}
                  </Stack>
                )}

                {result.injectedExperiment &&
                  !result.allMatches.some((m) => m.isInjected) && (
                    <>
                      <Separator className="my-2" />
                      <Stack gap={2}>
                        <Text size="xs" color="tertiary">
                          This experiment was tested but the user is not
                          allocated to it in PostHog.
                        </Text>
                      </Stack>
                    </>
                  )}
              </Stack>
            </div>
          )}
        </Stack>

        <DialogFooter>
          <Button
            onClick={handleTest}
            disabled={isLoading || !selectedTenant || !selectedUserId}
          >
            {isLoading ? 'Testing...' : 'Run Test'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
